import { IDisposable, toDisposable } from "../../../base/common/lifecycle.js";

// #region weak poll
// Per-node polls in the tree provider (child add/remove, visibility, name) that
// three.js doesn't reliably event. Each MUST hold its object + tree node WEAKLY
// and self-terminate once either is collected — otherwise the browser's timer
// keeps them (and the whole scene subtree: materials/textures) alive even after
// the tree node is dropped. A `SkinnedMesh` subtree (~165 MB) was retained by a
// DOMTimer this way; see plans/extension-host-retains-engine-objects.md.

/**
 * Run `tick` every `intervalMs`, holding `object` and `node` WEAKLY. The moment
 * either is collected the watcher drops itself — so the poll never keeps them
 * alive. `tick` receives the still-live pair.
 *
 * Watchers of the same cadence SHARE one interval. Every hierarchy node used to
 * install its own (a 300 ms visibility watch plus two 1 s syncs), so a 500-node
 * scene ran 1500 browser timers and woke the main thread ~2400 times a second,
 * each wake its own task — 47,786 callbacks in a 20 s trace. The work per node
 * is real; the scheduling around it was not.
 */
export function pollWhileAlive<O extends object, N extends object>(
    object: O,
    node: N,
    intervalMs: number,
    tick: (object: O, node: N) => void,
): IDisposable {
    return pollGroup(intervalMs).add({
        objectRef: new WeakRef(object),
        nodeRef: new WeakRef(node),
        tick: tick as (object: object, node: object) => void,
    });
}

interface PollEntry {
    readonly objectRef: WeakRef<object>;
    readonly nodeRef: WeakRef<object>;
    readonly tick: (object: object, node: object) => void;
}

/** one interval per cadence, shared by every watcher on it */
class PollGroup {
    private readonly entries = new Set<PollEntry>();
    private timer: ReturnType<typeof setInterval> | undefined;

    constructor(private readonly intervalMs: number) { }

    add(entry: PollEntry): IDisposable {
        this.entries.add(entry);
        this.start();
        return toDisposable(() => {
            this.entries.delete(entry);
            this.stopIfEmpty();
        });
    }

    private start(): void {
        if (this.timer !== undefined) return;
        this.timer = setInterval(() => this.run(), this.intervalMs);
    }

    private run(): void {
        // These polls exist to keep the hierarchy TREE honest. A hidden tab
        // renders no tree, so the work would be pure background cost on a page
        // we do not own — the state is re-read on the next visible tick anyway.
        if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
        // iterate a COPY: a tick can dispose its own watcher (or a sibling's)
        for (const entry of [...this.entries]) {
            const object = entry.objectRef.deref();
            const node = entry.nodeRef.deref();
            if (!object || !node) { this.entries.delete(entry); continue; } // collected → forget it
            entry.tick(object, node);
        }
        this.stopIfEmpty();
    }

    private stopIfEmpty(): void {
        if (this.entries.size > 0 || this.timer === undefined) return;
        clearInterval(this.timer);
        this.timer = undefined;
    }
}

const pollGroups = new Map<number, PollGroup>();
function pollGroup(intervalMs: number): PollGroup {
    let group = pollGroups.get(intervalMs);
    if (!group) pollGroups.set(intervalMs, group = new PollGroup(intervalMs));
    return group;
}
// #endregion

// #region child-hierarchy watch

/** Minimal shape the watch needs — an Object3D (or tree node) duck-typed. */
interface ChildBearing {
    readonly children: ReadonlyArray<{ readonly id: number }>;
}

/** Cheap 32-bit fingerprint of an object's direct children by id. A change means
 *  a child was added, removed, or reordered. */
export function hashChildIds(object: ChildBearing): string {
    let hash = 0;
    for (const child of object.children) {
        hash = ((hash << 5) - hash) + child.id;
        hash |= 0; // 32-bit
    }
    return hash.toString();
}

export type ChildWatchTick =
    | { action: "stop" }
    | { action: "notify"; hash: string }
    | { action: "idle"; hash: string };

/** Pure per-tick decision. `object`/`node` are the DEREFERENCED weak refs:
 *  `undefined` means the object or its tree node was collected → STOP. */
export function childWatchTick(object: ChildBearing | undefined, node: object | undefined, lastHash: string): ChildWatchTick {
    if (!object || !node) return { action: "stop" };
    const hash = hashChildIds(object);
    return hash === lastHash ? { action: "idle", hash } : { action: "notify", hash };
}

/** Watch `object`'s direct children for the add/remove three.js doesn't event, by
 *  polling their id-hash once a second (weak — see {@link pollWhileAlive}). */
export function watchChildHierarchy<O extends ChildBearing & object, N extends object>(
    object: O,
    node: N,
    onChanged: (object: O, node: N) => void,
): IDisposable {
    let lastHash = hashChildIds(object);
    return pollWhileAlive(object, node, 1000, (obj, n) => {
        const result = childWatchTick(obj, n, lastHash);
        if (result.action === "stop") return; // unreachable here (obj/n are live), kept exhaustive
        lastHash = result.hash;
        if (result.action === "notify") onChanged(obj, n);
    });
}
// #endregion
